import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, MessageCircle } from "lucide-react";
import { SEO } from "../components/SEO";
import { ProductCard } from "../components/ProductCard";
import { QuoteCTA } from "../components/CTA";
import { EnquiryForm } from "../components/EnquiryForm";
import { CONTACT, categoryName, getProduct, relatedProducts } from "../data/site";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = getProduct(slug);
  if (!product) {
    return (
      <div className="container notfound">
        <h1>Product not found</h1>
        <p>The product you're looking for doesn't exist or has moved.</p>
        <Link to="/products" className="btn btn-orange">View All Products <ArrowRight size={15} /></Link>
      </div>
    );
  }
  const related = relatedProducts(product);
  return (
    <>
      <SEO title={`${product.name} | CustoRacks — Customised Storage Solutions`}
        description={`${product.name} — ${product.short} CustoRacks designs, supplies and installs ${categoryName(product.category).toLowerCase()} around your requirement.`} />
      <section className="section" style={{ paddingTop: 72 }}>
        <div className="container">
          <div className="crumbs" style={{ color: "var(--muted)" }}>
            <Link to="/" style={{ color: "var(--blue)" }}>Home</Link> / <Link to="/products" style={{ color: "var(--blue)" }}>Products</Link> / {product.name}
          </div>
          <div className="pd-top" data-testid="product-detail">
            <div className="pd-img"><img src={product.image} alt={product.alt} /></div>
            <div className="pd-intro">
              <span className="card-cat">{categoryName(product.category)}</span>
              <h1>{product.name}</h1>
              <p>{product.intro}</p>
              <div className="pd-actions">
                <a href="#enquire" className="btn btn-orange" data-testid="pd-quote-btn">Get a Quote <ArrowRight size={15} /></a>
                <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="btn btn-whatsapp" data-testid="pd-whatsapp-btn"><MessageCircle size={15} /> WhatsApp Us</a>
              </div>
            </div>
          </div>

          <div className="pd-section grid-2">
            <div>
              <h2>Key Benefits</h2>
              <ul className="pd-list">
                {product.benefits.map((b) => <li key={b}><Check size={17} /> {b}</li>)}
              </ul>
            </div>
            <div>
              <h2>Typical Applications</h2>
              <ul className="pd-list">
                {product.applications.map((a) => <li key={a}><Check size={17} /> {a}</li>)}
              </ul>
            </div>
          </div>

          <div className="pd-section">
            <h2>Suitable Industries</h2>
            <div className="chip-row">{product.industries.map((i) => <span key={i}>{i}</span>)}</div>
          </div>

          <div className="pd-section">
            <h2>System Features</h2>
            <ul className="pd-list" style={{ maxWidth: 720 }}>
              {product.features.map((f) => <li key={f}><Check size={17} /> {f}</li>)}
            </ul>
          </div>

          <div className="pd-section">
            <h2>Gallery</h2>
            <div className="gallery-grid">
              {product.gallery.map((g, i) => <img key={i} src={g} alt={`${product.name} — image ${i + 1}`} loading="lazy" />)}
            </div>
          </div>

          {related.length > 0 && (
            <div className="pd-section">
              <h2>Related Products</h2>
              <div className="grid-3">{related.map((r) => <ProductCard key={r.slug} product={r} />)}</div>
            </div>
          )}

          <div className="pd-section" id="enquire" style={{ maxWidth: 760 }}>
            <h2>Enquire About {product.name}</h2>
            <p style={{ color: "var(--muted)", marginBottom: 26 }}>Share a few details and our team will help you plan the right system.</p>
            <EnquiryForm product={product.name} />
          </div>
        </div>
      </section>
      <QuoteCTA />
    </>
  );
}
